import HttpService from '../Service/HttpService'
import { getElementChild } from '../Utility/Dom'
import { IS_HIDDEN, IS_LOADING } from '../Utility/State'
import { FilterBar, FilterState } from './FilterBar'

export interface BaseTeaserItem {
  category: Category | null
  detailLink: string
  slug: string
  teaserImageData: TeaserImageData
  teaserImageVariants: TeaserImageVariants
  teaserText: string
  teaserTitle: string
  title: string
  uid: number
}

export interface Category {
  color: string
  title: string
  uid: number
}

export interface HydraResponse<T> {
  'hydra:member': T[]
  'hydra:totalItems': number
  'hydra:view'?: {
    'hydra:next'?: string
  }
}

export interface TeaserImageData {
  alternative: null | string
  description: null | string
  title: null | string
}

export interface TeaserImageVariants {
  teaser272x181: null | string
  teaser272x181_2x: null | string
  teaser440x294: null | string
  teaser440x294_2x: null | string
  teaser84x56: null | string
  teaser84x56_2x: null | string
}

export interface TeaserListConfig {
  apiUrl: string
  containerSelector: string
  itemsPerPage?: number
  listSelector: string
  templateSelector: string
}

export abstract class AbstractTeaserList<T extends BaseTeaserItem> {
  protected readonly apiUrl: string
  protected readonly container: HTMLElement
  protected currentCategories: number[] = []
  protected currentPage = 1
  protected currentSorting = 'date_desc'
  protected currentSubcategories: number[] = []
  protected readonly filterBar: FilterBar | null
  protected isLoading = false
  protected readonly isRecordMode: boolean
  protected readonly itemsPerPage: number
  protected readonly list: HTMLElement | null
  protected readonly loadingBar: HTMLElement | null
  protected readonly loadingBarButton: HTMLElement | null
  protected readonly loadingBarEnd: HTMLElement | null
  protected readonly loadingBarProgress: HTMLElement | null
  protected readonly loadingBarStart: HTMLElement | null
  protected readonly pageIds: number[]
  protected readonly recordIds: number[]
  protected readonly skeleton: HTMLElement | null
  protected readonly tagId: null | number
  protected readonly template: HTMLTemplateElement | null
  protected totalItems = 0

  constructor(container: HTMLElement, config: TeaserListConfig) {
    this.container = container
    this.apiUrl = config.apiUrl ?? ''
    this.itemsPerPage = config.itemsPerPage ?? 6

    this.pageIds = container.dataset.items
      ?.split(',')
      .map((id: string): number => parseInt(id.trim(), 10))
      .filter((id: number): boolean => !isNaN(id)) ?? []

    this.recordIds = container.dataset.recordIds
      ?.split(',')
      .map((id: string): number => parseInt(id.trim(), 10))
      .filter((id: number): boolean => !isNaN(id)) ?? []

    const tagId = parseInt(container.dataset.tag ?? '', 10)
    this.tagId = isNaN(tagId) ? null : tagId

    this.isRecordMode = this.recordIds.length > 0

    this.list = getElementChild(this.container, config.listSelector)
    this.loadingBar = getElementChild(this.container, 'loading-bar')
    this.loadingBarButton = getElementChild(this.loadingBar, 'loading-bar-button')
    this.loadingBarStart = getElementChild(this.loadingBar, 'loading-bar-start')
    this.loadingBarEnd = getElementChild(this.loadingBar, 'loading-bar-end')
    this.loadingBarProgress = getElementChild(this.loadingBar, 'loading-bar-progress')
    this.skeleton = getElementChild(this.container, 'skeleton')
    this.template = getElementChild(this.container, config.templateSelector) as HTMLTemplateElement | null

    const filterBarElement = getElementChild(this.container, 'filter-bar')

    if (filterBarElement !== null) {
      this.filterBar = new FilterBar(filterBarElement, (state: FilterState): void => {
        this.currentCategories = state.categories
        this.currentSorting = state.sorting
        this.currentSubcategories = state.subcategories
        this.currentPage = 1
        this.loadItems(true)
      })
      this.filterBar.init()
    } else {
      this.filterBar = null
    }

    const initialItemCount = this.list?.children.length ?? 0

    if (initialItemCount > 0) {
      this.currentPage = Math.ceil(initialItemCount / this.itemsPerPage)
    }

    this.addLoadMoreEvent()

    if (this.loadingBarEnd !== null) {
      this.totalItems = parseInt(this.loadingBarEnd.textContent ?? '0', 10)
      this.updateLoadingBar()
    }
  }

  protected buildApiUrl(): string {
    const params = new URLSearchParams()

    if (this.isRecordMode) {
      this.recordSlice(this.currentPage).forEach((uid: number): void => {
        params.append('uid[]', uid.toString())
      })

      return `${this.apiUrl}?${params.toString()}`
    }

    params.set('page', this.currentPage.toString())
    params.set('itemsPerPage', this.itemsPerPage.toString())

    this.currentCategories.forEach((id: number): void => {
      params.append('category[]', id.toString())
    })

    this.currentSubcategories.forEach((id: number): void => {
      params.append('subcategory[]', id.toString())
    })

    if (this.currentSorting !== 'date_desc') {
      params.set('sort', this.currentSorting)
    }

    if (this.tagId !== null) {
      params.set('tags', this.tagId.toString())

      return `${this.apiUrl}?${params.toString()}`
    }

    this.pageIds.forEach((pid: number): void => {
      params.append('pid[]', pid.toString())
    })

    return `${this.apiUrl}?${params.toString()}`
  }

  protected abstract createItem(item: T): HTMLElement | null

  protected async loadItems(replaceItems: boolean): Promise<void> {
    if (this.isLoading === true || this.list === null) {
      return
    }

    this.isLoading = true
    this.loadingBarButton?.classList.add(IS_LOADING)

    if (replaceItems === true) {
      this.list.innerHTML = ''
      this.list.style.display = 'none'
      this.showSkeleton()
    } else {
      this.appendSkeletonItems()
    }

    const url = this.buildApiUrl()

    try {
      const response = await HttpService.get(url) as HydraResponse<T>

      this.renderItems(this.isRecordMode ? this.orderBySelection(response['hydra:member']) : response['hydra:member'])
      this.totalItems = this.isRecordMode ? this.recordIds.length : response['hydra:totalItems']

      const hasMoreItems = this.isRecordMode
        ? this.currentPage * this.itemsPerPage < this.recordIds.length
        : response['hydra:view']?.['hydra:next'] !== undefined
      this.updateLoadingBar(this.totalItems, hasMoreItems)
    } catch (error) {
      console.error('Error loading items:', error)
    } finally {
      this.isLoading = false
      this.hideSkeleton()
      this.removeSkeletonItems()
      this.list.style.display = ''
      this.loadingBarButton?.classList.remove(IS_LOADING)
    }
  }

  protected orderBySelection(items: T[]): T[] {
    return [...items].sort((a: T, b: T): number => this.recordIds.indexOf(a.uid) - this.recordIds.indexOf(b.uid))
  }

  protected recordSlice(page: number): number[] {
    return this.recordIds.slice((page - 1) * this.itemsPerPage, page * this.itemsPerPage)
  }

  protected truncateText(text: string, maxLength: number): string {
    const strippedText = text.replace(/<[^>]*>/g, '')

    if (strippedText.length <= maxLength) {
      return strippedText
    }

    return strippedText.substring(0, maxLength).trim() + '…'
  }

  protected updatePictureSources(picture: HTMLElement, item: T): boolean {
    const variants = item.teaserImageVariants

    if (!variants) {
      picture.remove()

      return false
    }

    const sources = picture.querySelectorAll('source')
    const img = picture.querySelector('img')
    const imageData = item.teaserImageData

    sources.forEach((source: HTMLSourceElement): void => {
      const media = source.getAttribute('media')

      if ((media === '(min-width: 321px)' || media === '(min-width: 360px)' || media === '(min-width: 768px)' || media === '(min-width: 1440px)') && variants.teaser440x294) {
        source.srcset = `${variants.teaser440x294} 1x${variants.teaser440x294_2x ? `, ${variants.teaser440x294_2x} 2x` : ''}`
      } else if ((media === null || media === '') && variants.teaser272x181) {
        source.srcset = `${variants.teaser272x181} 1x${variants.teaser272x181_2x ? `, ${variants.teaser272x181_2x} 2x` : ''}`
      }
    })

    if (img !== null && variants.teaser440x294) {
      img.src = variants.teaser440x294
      img.srcset = `${variants.teaser440x294} 1x${variants.teaser440x294_2x ? `, ${variants.teaser440x294_2x} 2x` : ''}`
      img.alt = imageData?.alternative || item.teaserTitle || item.title

      return true
    }

    picture.remove()

    return false
  }

  private addLoadMoreEvent(): void {
    if (this.loadingBarButton === null) {
      return
    }

    this.loadingBarButton.addEventListener('click', (): void => {
      if (this.isLoading === true) {
        return
      }

      this.currentPage++
      this.loadItems(false)
    })
  }

  private appendSkeletonItems(): void {
    if (this.list === null || this.skeleton === null) {
      return
    }

    const skeletonItem = this.skeleton.querySelector('.skeleton__item')

    if (skeletonItem === null) {
      return
    }

    for (let i = 0; i < this.itemsPerPage; i++) {
      const clone = skeletonItem.cloneNode(true) as HTMLElement
      clone.dataset.skeleton = 'true'
      this.list.appendChild(clone)
    }
  }

  private hideSkeleton(): void {
    this.container.classList.remove(IS_LOADING)
  }

  private removeSkeletonItems(): void {
    if (this.list === null) {
      return
    }

    this.list.querySelectorAll('[data-skeleton="true"]').forEach((el: Element): void => {
      el.remove()
    })
  }

  private renderItems(items: T[]): void {
    if (this.list === null || this.template === null) {
      return
    }

    this.removeSkeletonItems()

    items.forEach((item: T): void => {
      const element = this.createItem(item)

      if (element !== null) {
        this.list?.appendChild(element)
      }
    })
  }

  private showSkeleton(): void {
    this.container.classList.add(IS_LOADING)
  }

  private updateLoadingBar(totalItems?: number, hasMoreItems?: boolean): void {
    if (this.loadingBar === null) {
      return
    }

    const total = totalItems ?? this.totalItems
    const currentCount = this.list?.children.length ?? 0

    if (this.loadingBarStart !== null) {
      this.loadingBarStart.textContent = currentCount.toString()
    }

    if (this.loadingBarEnd !== null) {
      this.loadingBarEnd.textContent = total.toString()
    }

    if (this.loadingBarProgress !== null) {
      const progress = total > 0 ? (currentCount / total) * 100 : 0
      this.loadingBarProgress.setAttribute('style', `--progress:${progress}%`)
    }

    const allItemsLoaded = hasMoreItems === false || currentCount >= total

    if (allItemsLoaded) {
      this.loadingBarButton?.classList.add(IS_HIDDEN)
    } else {
      this.loadingBarButton?.classList.remove(IS_HIDDEN)
    }
  }
}
